import { ToastContainer } from 'react-toastify';
import Footer from './components/Footer';
import { ModalProvider } from './components/PreLaunch/ModalContext';
import Navbar from './components/Navbar';
import Modal from './components/PreLaunch/Modal';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Modals from './views/Modals';
import Gifts from './views/Gifts';
import AppLayout from './layouts/AppLayout';
import CreateGift from './views/Gifts/CreateGift';
import GiftsList from './views/Gifts/GiftsList';
import GiftDetails from './views/Gifts/Gift/index.jsx';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import { useEffect, useState } from 'react';
import { UPDATE_BALANCE } from './common/constants.js';
import { useSolana } from './hooks/useSolana.js';
import { closeModal, openModal } from './redux/slices/modals.js';
import { useDispatch, useSelector } from 'react-redux';
import GiftClaimRouter from './views/Gifts/Gift/GiftClaimRouter';
import Status from './views/Gifts/Gift/Status';
import EditGift from './views/Gifts/EditGift';
import styled, { useTheme } from 'styled-components';
import { selectSolanaUSDCBalance } from './redux/slices/walletSlice';
import { selectThemeMode } from './redux/slices/themeSlice';
import { useGetDefaultTokensQuery } from './redux/api/public/tokens.js';
import Dashboard from './views/Dashboard/index.jsx';
import CheckEmail from './views/Gifts/Gift/CheckEmail.jsx';
import SurveyPromo from './components/PreLaunch/SurveyPromo';
import ClaimSurveyGift from './views/Gifts/Gift/ClaimSurveyGift.jsx';
import ClaimPromoCodeGift from './views/Gifts/Gift/ClaimPromoCodeGift';
import { isAccessingFromEurope } from './common/region.js';
import {
  selectPrivyToken,
  setIsEurope,
  setPrivyToken,
} from './redux/slices/auth.js';
import Partners from './components/Partners/index';
import Referrals from './components/Referrals/index';
import BridgeCallback from './components/BridgeCallback';
import BridgeKycCallback from './components/BridgeKycCallback';
import TokenSubmission from './views/TokenSubmission/index.jsx';
import TokenSubmissionResponse from './views/TokenSubmissionResponse/index.jsx';
import RedirectComponent from './components/RedirectComponent/index.jsx';
import { usePrivy, useUser } from '@privy-io/react-auth';
import TokenHome from './views/TokenHome/index.jsx';
import {
  useLazyGetCurrentUserProfileQuery,
  useSetFirebaseTokenMutation,
} from './redux/api/authorized/auth.js';
import Home from './views/Home/index.jsx';
import MobileNavigation from './components/MobileNavigation/index.jsx';
import Pricing from './views/Pricing/index.jsx';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import Admin from './views/Admin/index.jsx';
import Trade from './views/Trade/index.jsx';
import { killPreloader } from './common/loading.js';
import RewardsView from './views/Rewards/index.jsx';
import LandingV2 from './views/LandingV2/index.jsx';
import { initPush } from './common/firebase.js';
import MobileLogin from './views/Mobile/Login/index.jsx';
import { useSystemTheme } from './hooks/useSystemTheme.js';
import '@ionic/react/css/core.css';
import theme from './common/theme.js';
import { ThemeProvider } from 'styled-components';
import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonMenu,
  IonTitle,
  IonToolbar,
  setupIonicReact,
} from '@ionic/react';
import DropdownContent from './components/NavUserProfile/DropdownContent.jsx';
import { useCreateMockUserMutation } from './redux/api/authorized/privy';
import { closeOutline } from 'ionicons/icons';
import { NotificationsContent } from './components/NavUserProfile/Notifications/index.jsx';
import { menuController } from '@ionic/core/components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from './common/responsive.js';
import WalletProvider from './components/WalletProvider';
import DownloadApp from './views/DownloadApp/index.jsx';
import { hapticsImpactLight } from './common/haptic.js';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import Physical from './views/Physical/index.jsx';
import { GiftFormProvider } from './components/GiftFormContext/index.jsx';
import { useTokenSearch } from './hooks/useTokenSearch.js';
import Cards from './views/Cards/index.jsx';
import UpdateRequired from './views/UpdateRequired/index.jsx';
import { useCheckAppUpdateMutation } from './redux/api/public/app.js';

setupIonicReact();

const LandingContainer = styled.div`
  img {
    max-width: 100%;
  }
`;

function ReactApp() {
  const { refreshTokenBalances, embeddedWallet } = useSolana();
  const dispatch = useDispatch();
  const usdcBalance = useSelector(selectSolanaUSDCBalance);
  const { authenticated, ready, getAccessToken } = usePrivy();
  const { user: privyUser, refreshUser } = useUser();
  const location = useLocation();
  const privyToken = useSelector(selectPrivyToken);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRetries, setCurrentUserRetries] = useState(0);

  const { displayTokens } = useTokenSearch();

  const [getCurrentUserProfile] = useLazyGetCurrentUserProfileQuery();

  const [setFirebaseToken] = useSetFirebaseTokenMutation();

  const navigate = useNavigate();

  useEffect(() => {
    const setToken = async () => {
      const token = await getAccessToken();
      dispatch(setPrivyToken(token));
    };

    if (authenticated && !privyToken) {
      setToken();
    }
  }, [authenticated, getAccessToken, privyToken, dispatch]);

  useEffect(() => {
    let loop;
    if (authenticated) {
      loop = setInterval(async () => {
        const newToken = await getAccessToken();
        if (newToken !== privyToken) {
          dispatch(setPrivyToken(newToken));
        }
      }, 1000);
    }
    return () => {
      if (loop) {
        clearInterval(loop);
      }
    };
  }, [authenticated, getAccessToken, privyToken]);

  const [createMockUser] = useCreateMockUserMutation();
  const [checkAppUpdate] = useCheckAppUpdateMutation();

  useEffect(() => {
    const getCurrentUser = async () => {
      if (privyUser && privyToken && !currentUser) {
        if (currentUserRetries > 5) {
          return;
        }
        const resp = await getCurrentUserProfile();

        if (resp.isError) {
          if (import.meta.env.DEV) {
            createMockUser({ privyId: privyUser.id });
          }
          await new Promise((resolve) =>
            setTimeout(resolve, (currentUserRetries + 1) * 200),
          );
          setCurrentUserRetries(currentUserRetries + 1);
        } else {
          setCurrentUser(resp.data);
          if (!embeddedWallet) {
            refreshUser();
          }
        }
      }
    };

    getCurrentUser();
  }, [
    privyUser,
    privyToken,
    currentUserRetries,
    createMockUser,
    currentUser,
    embeddedWallet,
  ]);

  useSystemTheme();

  useEffect(() => {
    App.addListener('appUrlOpen', (event) => {
      // Support deep links and universal links
      const slug = event.url
        .replace('https://deks.xyz', '')
        .replace('http://deks.xyz', '')
        .replace('deks:/', '');
      if (slug) {
        window.location.replace(slug);
      }
    });
  }, []);

  useEffect(() => {
    if (
      privyToken &&
      setFirebaseToken &&
      navigate &&
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('FirebaseMessaging')
    ) {
      initPush((token) => {
        setFirebaseToken(token);
      }, navigate);
    }
  }, [setFirebaseToken, privyToken, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const referralCode = searchParams.get('rc');
    if (referralCode) {
      localStorage.setItem('rc', referralCode);
    }
  }, [location.search]);

  useGetDefaultTokensQuery();

  useEffect(() => {
    (async () => {
      const isEurope = await isAccessingFromEurope();
      dispatch(setIsEurope(isEurope));
    })();
  }, []);

  useEffect(() => {
    const listener = window.addEventListener('message', async (e) => {
      if (e?.data?.type === UPDATE_BALANCE) {
        await refreshTokenBalances();
        dispatch(closeModal());
      }
      if (
        e?.data &&
        e?.data?.includes &&
        e?.data?.includes('success') &&
        e?.origin === 'https://pay.coinbase.com'
      ) {
        dispatch(
          openModal({
            name: 'detectDeposit',
            props: { initialBalance: usdcBalance },
          }),
        );
      }
    });
    return removeEventListener('message', listener);
  }, [usdcBalance]);

  useEffect(() => {
    if (ready) {
      const searchParams = new URLSearchParams(location.search);
      const giftCode = searchParams.get('gift');
      if (giftCode) {
        // If there is a gift code, wait for the redirect to happen.
        return;
      }
      if (authenticated && currentUser) {
        killPreloader();
      } else if (!authenticated) {
        killPreloader();
      }
    }
  }, [authenticated, ready, currentUser, location.search]);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const Container = isMobile ? IonApp : 'div';

  const currentTheme = useTheme();
  const themeMode = useSelector(selectThemeMode);
  const { flagsReady } = useFlagsStatus();
  const appRequiredForClaim = useFlag('appRequiredForClaim');
  const isApp = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!flagsReady) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const modalName = searchParams.get('modal');
    const giftCode = searchParams.get('gift');
    // Handle modal query parameter
    if (authenticated && privyToken && modalName) {
      dispatch(openModal({ name: modalName }));
    }

    // Handle gift redirect logic
    if (giftCode) {
      // If the app is required for claim and the user is on mobile and not on the app, show the download page
      const shouldShowDownload = appRequiredForClaim && isMobile && !isApp;
      navigate(
        shouldShowDownload ? '/download' : `/app/gifts/${giftCode}/promo-claim`,
        {
          replace: true,
        },
      );
    }
  }, [
    authenticated,
    privyToken,
    location.search,
    dispatch,
    navigate,
    appRequiredForClaim,
    flagsReady,
  ]);

  useEffect(() => {
    if (Capacitor.isNativePlatform() && ready && !authenticated) {
      if (
        location.pathname !== '/' &&
        location.pathname !== '/download' &&
        !location.pathname.includes('/app/gifts/')
      ) {
        navigate('/');
      }
    }
  }, [theme, authenticated, ready]);

  useEffect(() => {
    const checkForUpdates = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App')) {
        try {
          const appInfo = await App.getInfo();
          const platform = Capacitor.getPlatform();
          const result = await checkAppUpdate({
            os: platform,
            appVersion: appInfo?.version,
          }).unwrap();

          if (result.requiresUpdate) {
            navigate('/update-required', { replace: true });
          }
        } catch (error) {
          console.error('Error checking for app updates:', error);
        }
      }
    };

    checkForUpdates();
  }, [checkAppUpdate, navigate]);

  return (
    !(!currentUser && authenticated) && (
      <GiftFormProvider>
        <Container
          style={{
            backgroundColor: currentTheme.background.primary,
            color: currentTheme.text.regular,
          }}
        >
          <ModalProvider>
            <Routes>
              <Route path='/download' element={<DownloadApp />} />
              <Route path='/update-required' element={<UpdateRequired />} />
              <Route path='/login' element={<MobileLogin />} />
              {Capacitor.isNativePlatform() ? (
                authenticated ? (
                  <Route path='/' element={<AppLayout />}>
                    <Route path='/' element={<Dashboard />} />
                  </Route>
                ) : (
                  <Route path='/' element={<MobileLogin />} />
                )
              ) : (
                <Route path='/' element={<AppLayout />}>
                  <Route path='/' element={<LandingV2 />} />
                </Route>
              )}
              <Route path='/rewards' element={<AppLayout />}>
                <Route path='/rewards' element={<RewardsView />} />
              </Route>
              <Route path='/pricing' element={<AppLayout />}>
                <Route path='/pricing' element={<Pricing />} />
              </Route>
              <Route path='/partners' element={<AppLayout />}>
                <Route path='/partners' element={<Partners />} />
              </Route>
              {currentUser?.role === 'ADMIN' && (
                <Route path='/admin' element={<AppLayout />}>
                  <Route path='/admin' element={<Admin />} />
                </Route>
              )}
              <Route path='/gift-cards' element={<AppLayout />}>
                <Route path='/gift-cards' element={<Physical />} />
              </Route>
              <Route path='/referrals' element={<Referrals />} />
              <Route path='/bridge-tos-callback' element={<BridgeCallback />} />
              <Route
                path='/bridge-kyc-complete'
                element={<BridgeKycCallback />}
              />
              <Route path='/gifts'>
                <Route path=':id'>
                  <Route path='claim' element={<CheckEmail />} />
                  <Route path='survey-claim' element={<CheckEmail />} />
                </Route>
              </Route>
              <Route
                path='/submission'
                element={<RedirectComponent to='/app/token-submission' />}
              />
              <Route
                path='/halloween'
                element={
                  <RedirectComponent to='/app/tokens/XsbEhLAtcf6HdfpFZ5xEMdqW8nfAvcsP5bdudRLJzJp?showGift=true' />
                }
              />
              <Route path='/terms' element={<TermsOfUse />} />
              <Route path='/privacy' element={<PrivacyPolicy />} />
              <Route
                path='survey'
                element={
                  <LandingContainer>
                    <Navbar />
                    <SurveyPromo />
                    <Footer />
                  </LandingContainer>
                }
              />
              <Route path='/app' element={<AppLayout />}>
                <Route path='home' element={<Dashboard />} />
                <Route path='cards' element={<Cards />} />
                <Route path='discover' element={<Home />} />
                <Route path='portfolio' element={<Dashboard />} />
                <Route path='token-submission' element={<TokenSubmission />} />
                <Route
                  path='token-submission/:id/:action'
                  element={<TokenSubmissionResponse />}
                />
                <Route path='gifts' element={<Gifts />}>
                  <Route index element={<GiftsList />} />
                  <Route path='new' element={<CreateGift />} />
                  <Route path=':id'>
                    <Route
                      path='promo-claim'
                      element={<ClaimPromoCodeGift />}
                    />
                    <Route element={<GiftDetails />}>
                      <Route index element={<GiftClaimRouter />} />
                      <Route path='edit' element={<EditGift />} />
                      <Route path='claim' element={<GiftClaimRouter />} />
                      <Route
                        path='survey-claim'
                        element={<ClaimSurveyGift />}
                      />
                      <Route path='status' element={<Status />} />
                    </Route>
                  </Route>
                </Route>
                <Route path='tokens/:address' element={<TokenHome />} />
                <Route path='trade' element={<Trade />} />
              </Route>
            </Routes>
            <MobileNavigation />
            <Modals />
            <Modal />
          </ModalProvider>
          <IonMenu
            contentId='main-content'
            swipeGesture={false}
            style={{
              '--background': currentTheme.background.primary,
            }}
          >
            <IonContent
              className='ion-padding'
              style={{
                '--background': currentTheme.background.primary,
              }}
            >
              <DropdownContent />
            </IonContent>
          </IonMenu>
          <IonMenu
            style={{
              '--width': '100vw',
              '--background': currentTheme.background.primary,
            }}
            menuId='notifications'
            contentId='main-content'
            side='end'
            swipeGesture={false}
          >
            <IonHeader>
              <IonToolbar
                style={{
                  '--min-height': Capacitor.isNativePlatform()
                    ? '66px'
                    : '76px',
                  '--background': currentTheme.background.primary,
                }}
              >
                <IonTitle
                  style={{
                    color: currentTheme.text.title,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 34,
                      width: '100%',
                      color: currentTheme.text.title,
                    }}
                  >
                    Notifications
                  </div>
                </IonTitle>
                <IonButtons slot='end'>
                  <IonButton
                    style={{
                      '--color': currentTheme.text.title,
                    }}
                    aria-label='Close menu'
                    onClick={() => {
                      hapticsImpactLight();
                      menuController.toggle('notifications');
                    }}
                  >
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent
              className='ion-padding'
              style={{
                '--background': currentTheme.background.primary,
              }}
            >
              <NotificationsContent />
            </IonContent>
          </IonMenu>
          <ToastContainer
            theme={themeMode}
            style={{ zIndex: 99999 }}
            toastStyle={{ zIndex: 99999 }}
          />
        </Container>
      </GiftFormProvider>
    )
  );
}

const lightModeConfig = {
  theme: 'light',
  logo: '/logo-footer.webp',
};

const darkModeConfig = {
  theme: 'dark',
  logo: '/logo-footer-dark.webp',
};

const ThemedApp = () => {
  const themeMode = useSelector(selectThemeMode);

  useEffect(() => {
    document.body.style.backgroundColor =
      theme.colors[themeMode].background.primary;
    document.body.style.color = theme.colors[themeMode].text.regular;
  }, [themeMode]);

  return (
    <ThemeProvider theme={theme.colors[themeMode]}>
      <WalletProvider
        appearance={themeMode === 'light' ? lightModeConfig : darkModeConfig}
      >
        <ReactApp />
      </WalletProvider>
    </ThemeProvider>
  );
};

export default ThemedApp;
